import { defaultMultipleSelectProps, ValueContainer } from "components/shared/select";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { reportFilterWrapper, SelectColumnFilter } from "helpers/reactTable";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import React from "react";
import { list as driversList } from "sources/books/drivers";
import { list as logistsList } from "sources/books/employees";
import { list as trucksList } from "sources/books/trucks";
import CommonReport from "./commonReport";

export default class MarginalityReport extends CommonReport {
    formatData = (data) => {
        return data.reduce((res, rowData) => {
            const { activityType, logists, ...activityRow } = rowData;
            const { logistCount } = activityRow;
            if (logistCount > 0) {
                logists.forEach((logistData) => {
                    const { carLogistId, carLogistDisplayName, cars, ...logistRow } = logistData;
                    const { carCount } = logistRow;
                    if (carCount > 0) {
                        cars.forEach(({ carId, carNumber, drivers, ...carRow }) => {
                            const { driverCount } = carRow;
                            if (driverCount > 0) {
                                drivers.forEach(({ bids, driverId, driverDisplayName, ...driverRow }) => {
                                    const { bidCount } = driverRow;
                                    if (bidCount > 0) {
                                        bids.forEach((bidRow) => {
                                            res.push({
                                                ...bidRow,
                                                logistCount,
                                                carCount,
                                                driverCount,
                                                bidCount,
                                                activityType,
                                                carLogistId,
                                                carLogistDisplayName,
                                                carId,
                                                carNumber,
                                                driverId,
                                                driverDisplayName,
                                                sum: { 0: activityRow, 1: logistRow, 2: carRow, 3: driverRow },
                                            });
                                        });
                                    } else {
                                        res.push({
                                            ...driverRow,
                                            logistCount,
                                            carCount,
                                            driverCount,
                                            bidCount,
                                            activityType,
                                            carLogistId,
                                            carLogistDisplayName,
                                            carId,
                                            carNumber,
                                            driverId,
                                            driverDisplayName,
                                            sum: { 0: activityRow, 1: logistRow, 2: carRow, 3: driverRow },
                                        });
                                    }
                                });
                            } else {
                                res.push({
                                    ...carRow,
                                    logistCount,
                                    carCount,
                                    driverCount,
                                    activityType,
                                    carLogistId,
                                    carLogistDisplayName,
                                    carId,
                                    carNumber,
                                    sum: { 0: activityRow, 1: logistRow, 2: carRow },
                                });
                            }
                        });
                    } else {
                        res.push({
                            ...logistRow,
                            logistCount,
                            carCount,
                            activityType,
                            carLogistId,
                            carLogistDisplayName,
                            sum: { 0: activityRow, 1: logistRow },
                        });
                    }
                });
            }
            return res;
        }, []);
    };

    aggregatedValue = (leafRows, level, columnName) => {
        return leafRows[0].original.sum[level]?.[columnName];
    };

    columnClrType = (propertyName) => {
        const { columns } = this.props.data || {};
        const column = columns.find((item) => columnName(item) === propertyName);
        return column ? column.clrType : null;
    };

    extractColumns = () => {
        const { organization } = this.props;
        return [
            { accessor: "carLogistDisplayName" },
            { accessor: "carNumber" },
            { accessor: "driverDisplayName" },
            {
                Header: (
                    <>
                        Тип активности <br />в выбранном
                        <br />
                        периоде
                    </>
                ),
                accessor: "activityType",
                filterable: false,
                Cell: (props) => <span>{props.value}</span>,
                Footer: () => <b>ИТОГО:</b>,
            },
            {
                Header: "Логист",
                accessor: "carLogistId",
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            url: logistsList,
                            inputProps: { name: "carLogistIds" },
                            propName: "fullNameOrEmail",
                            components: { ValueContainer },
                        }}
                    />
                )),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                        return rows;
                    }
                    return rows.filter((row) => filterValue.value.includes(row.values.carLogistId));
                },
                IsGrouped: (props) => {
                    const { leafRows } = props.row;
                    return props.isGrouped && leafRows[0].original.carCount > 0;
                },
                Aggregated: ({ row: { leafRows, depth, isGrouped } }) => {
                    if (depth > 0 || !isGrouped) {
                        return null;
                    }
                    return (
                        <strong>{formatValue(this.aggregatedValue(leafRows, depth, "logistCount"), "int32")}</strong>
                    );
                },
                IsAggregated: () => true,
                Cell: (props) => {
                    const { leafRows } = props.row;
                    return (
                        <span data-logist={leafRows[0].original.carLogistId}>
                            {leafRows[0].original.carLogistDisplayName || "Без логиста"}
                        </span>
                    );
                },
                Footer: () => this.footerVal({ propertyName: "LogistCount", aggregateType: true }),
            },
            {
                Header: "Машина",
                accessor: "carId",
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            url: trucksList,
                            inputProps: { name: "carId" },
                            propName: "number",
                            components: { ValueContainer },
                        }}
                    />
                )),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                        return rows;
                    }
                    return rows.filter((row) => filterValue.value.includes(row.values.carId));
                },
                IsGrouped: (props) => {
                    const { leafRows } = props.row;
                    return props.isGrouped && leafRows[0].original.driverCount > 0;
                },
                Aggregated: ({ row: { leafRows, depth, isGrouped } }) => {
                    if (depth > 1 || !isGrouped) {
                        return null;
                    }
                    return <strong>{formatValue(this.aggregatedValue(leafRows, depth, "carCount"), "int32")}</strong>;
                },
                IsAggregated: () => true,
                Cell: (props) => {
                    const { leafRows } = props.row;
                    return <span data-car={leafRows[0].original.carId}>{leafRows[0].original.carNumber}</span>;
                },
                Footer: () => this.footerVal({ propertyName: "CarCount", aggregateType: true }),
            },
            {
                ...typeSpecificProps({}),
                Header: "Водитель",
                accessor: "driverId",
                Filter: reportFilterWrapper((props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            url: driversList,
                            inputProps: { name: "driverId" },
                            propName: "user.fullName",
                            filterPropName: "user/fullName",
                            components: { ValueContainer },
                        }}
                    />
                )),
                filter: (rows, _id, filterValue) => {
                    if (!filterValue || !filterValue.value || filterValue.value.length === 0) {
                        return rows;
                    }
                    return rows.filter((row) => filterValue.value.includes(row.values.driverId));
                },
                IsGrouped: (props) => {
                    const { leafRows } = props.row;
                    return props.isGrouped && leafRows[0].original.bidCount > 0;
                },
                Aggregated: ({ row: { leafRows, depth, isGrouped } }) => {
                    if (depth > 2 || !isGrouped) {
                        return null;
                    }
                    return (
                        <strong>{formatValue(this.aggregatedValue(leafRows, depth, "driverCount"), "int32")}</strong>
                    );
                },
                IsAggregated: () => true,
                Cell: (props) => {
                    const { leafRows } = props.row;
                    return (
                        <span data-driver={leafRows[0].original.driverId}>
                            {leafRows[0].original.driverDisplayName} ({props.row.leafRows[0].original.bidCount || 0})
                        </span>
                    );
                },
                Footer: () => this.footerVal({ propertyName: "DriverCount", aggregateType: true }),
            },
            {
                Header: "Заявка",
                accessor: "bidId",
                ...typeSpecificProps({}),
                Cell: ({ value }) => (
                    <a data-bidid={value} href={paths.bidShow("bid", value)} target="_blank" rel="noopener noreferrer">
                        {value}
                    </a>
                ),
                Aggregated: ({ row: { leafRows, depth, isGrouped } }) => {
                    if (depth > 3 || !isGrouped) {
                        return null;
                    }
                    return <strong>{formatValue(this.aggregatedValue(leafRows, depth, "bidCount"), "int32")}</strong>;
                },
                Footer: () => this.footerVal({ propertyName: "BidCount", aggregateType: true }),
            },
            {
                Header: "Плановые значения",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "datetimeoffset" }),
                        Header: "Дата начала",
                        accessor: "plannedStartDate",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "PlannedStartDate",
                                aggregateType: true,
                                clrType: this.columnClrType("plannedStartDate"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "plannedStartDate"),
                                    this.columnClrType("plannedStartDate"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-planned-start-date={values.bidId}>
                                {formatValue(value, this.columnClrType("plannedStartDate"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "datetimeoffset" }),
                        Header: "Дата окончания",
                        accessor: "plannedEndDate",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "PlannedEndDate",
                                aggregateType: true,
                                clrType: this.columnClrType("plannedEndDate"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "plannedEndDate"),
                                    this.columnClrType("plannedEndDate"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-planned-end-date={values.bidId}>
                                {formatValue(value, this.columnClrType("plannedEndDate"))}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "Факт. (расчетные) значения",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "datetimeoffset" }),
                        Header: "Дата начала",
                        accessor: "factOrEstimatedStartDate",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "FactOrEstimatedStartDate",
                                aggregateType: true,
                                clrType: this.columnClrType("factOrEstimatedStartDate"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "factOrEstimatedStartDate"),
                                    this.columnClrType("factOrEstimatedStartDate"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-fact-or-estimated-start-date={values.bidId}>
                                {formatValue(value, this.columnClrType("factOrEstimatedStartDate"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "datetimeoffset" }),
                        Header: "Дата окончания",
                        accessor: "factOrEstimatedEndDate",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "FactOrEstimatedEndDate",
                                aggregateType: true,
                                clrType: this.columnClrType("factOrEstimatedEndDate"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "factOrEstimatedEndDate"),
                                    this.columnClrType("factOrEstimatedEndDate"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-fact-or-estimatedend-date={values.bidId}>
                                {formatValue(value, this.columnClrType("factOrEstimatedEndDate"))}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "ВАЛ",
                accessor: "output",
                ...typeSpecificProps({ clrType: "double" }),
                Cell: ({ value }) =>
                    hiddenUserData(formatValue(value, this.columnClrType("output")), { type: "price" }),
                Aggregated: ({ row: { leafRows, depth } }) => {
                    return (
                        <strong>
                            {hiddenUserData(
                                formatValue(
                                    this.aggregatedValue(leafRows, depth, "output"),
                                    this.columnClrType("output"),
                                ),
                                {
                                    type: "price",
                                },
                            )}
                        </strong>
                    );
                },
                Footer: () =>
                    this.footerVal({
                        propertyName: "Output",
                        aggregateType: true,
                        clrType: this.columnClrType("output"),
                    }),
            },
            {
                Header: "Себестоимость маршрута",
                accessor: "routeCost",
                ...typeSpecificProps({ clrType: "double" }),
                Cell: ({ value }) =>
                    hiddenUserData(formatValue(value, this.columnClrType("routeCost")), { type: "price" }),
                Aggregated: ({ row: { leafRows, depth } }) => {
                    return (
                        <strong>
                            {hiddenUserData(
                                formatValue(
                                    this.aggregatedValue(leafRows, depth, "routeCost"),
                                    this.columnClrType("routeCost"),
                                ),
                                {
                                    type: "price",
                                },
                            )}
                        </strong>
                    );
                },
                Footer: () =>
                    this.footerVal({
                        propertyName: "RouteCost",
                        aggregateType: true,
                        clrType: this.columnClrType("routeCost"),
                    }),
            },
            {
                Header: "Маржа",
                accessor: "margin",
                ...typeSpecificProps({ clrType: "double" }),
                Cell: ({ value }) =>
                    hiddenUserData(formatValue(value, this.columnClrType("margin")), { type: "price" }),
                Aggregated: ({ row: { leafRows, depth } }) => {
                    return (
                        <strong>
                            {hiddenUserData(
                                formatValue(
                                    this.aggregatedValue(leafRows, depth, "margin"),
                                    this.columnClrType("margin"),
                                ),
                                {
                                    type: "price",
                                },
                            )}
                        </strong>
                    );
                },
                Footer: () =>
                    this.footerVal({
                        propertyName: "Margin",
                        aggregateType: true,
                        clrType: this.columnClrType("margin"),
                    }),
            },
            {
                Header: "Рентабельность",
                accessor: "profitability",
                ...typeSpecificProps({ clrType: "double" }),
                Cell: ({ value }) =>
                    hiddenUserData(formatValue(value, this.columnClrType("profitability")), { type: "percent" }),
                Aggregated: ({ row: { leafRows, depth } }) => {
                    return (
                        <strong>
                            {hiddenUserData(
                                formatValue(
                                    this.aggregatedValue(leafRows, depth, "profitability"),
                                    this.columnClrType("profitability"),
                                ),
                                {
                                    type: "percent",
                                },
                            )}
                        </strong>
                    );
                },
                Footer: () =>
                    this.footerVal({
                        propertyName: "Profitability",
                        aggregateType: true,
                        clrType: this.columnClrType("profitability"),
                    }),
            },
            {
                Header: "Плановые значения",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Кол-во дней на маршруте",
                        accessor: "plannedNumberOfDays",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "PlannedNumberOfDays",
                                aggregateType: true,
                                clrType: this.columnClrType("plannedNumberOfDays"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "plannedNumberOfDays"),
                                    this.columnClrType("plannedNumberOfDays"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-planned-number-of-days={values.bidId}>
                                {formatValue(value, this.columnClrType("plannedNumberOfDays"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: `Маржа в день, ${organization.currencyData?.name}/день`,
                        accessor: "plannedMarginPerDay",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "PlannedMarginPerDay",
                                aggregateType: true,
                                clrType: this.columnClrType("plannedMarginPerDay"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {hiddenUserData(
                                    formatValue(
                                        this.aggregatedValue(leafRows, depth, "plannedMarginPerDay"),
                                        this.columnClrType("plannedMarginPerDay"),
                                    ),
                                    { type: "pricePerVal" },
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-planned-margin-per-day={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("plannedMarginPerDay")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "Расчетные значения",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Маржа",
                        accessor: "estimatedMargin",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "EstimatedMargin",
                                aggregateType: true,
                                clrType: this.columnClrType("estimatedMargin"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "estimatedMargin"),
                                    this.columnClrType("estimatedMargin"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-estimated-margin={values.bidId}>
                                {formatValue(value, this.columnClrType("estimatedMargin"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Кол-во дней на маршруте",
                        accessor: "estimatedNumberOfDays",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "EstimatedNumberOfDays",
                                aggregateType: true,
                                clrType: this.columnClrType("estimatedNumberOfDays"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "estimatedNumberOfDays"),
                                    this.columnClrType("estimatedNumberOfDays"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-estimated-number-of-days={values.bidId}>
                                {formatValue(value, this.columnClrType("estimatedNumberOfDays"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: `Маржа в день, ${organization.currencyData?.name}/день`,
                        accessor: "estimatedMarginPerDay",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "EstimatedMarginPerDay",
                                aggregateType: true,
                                clrType: this.columnClrType("estimatedMarginPerDay"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {hiddenUserData(
                                    formatValue(
                                        this.aggregatedValue(leafRows, depth, "estimatedMarginPerDay"),
                                        this.columnClrType("estimatedMarginPerDay"),
                                    ),
                                    { type: "pricePerVal" },
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-estimated-margin-per-day={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("estimatedMarginPerDay")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "Фактические значения",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Маржа",
                        accessor: "factMargin",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "FactMargin",
                                aggregateType: true,
                                clrType: this.columnClrType("factMargin"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "factMargin"),
                                    this.columnClrType("factMargin"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-fact-margin={values.bidId}>
                                {formatValue(value, this.columnClrType("factMargin"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Кол-во дней на маршруте",
                        accessor: "factNumberOfDays",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "FactNumberOfDays",
                                aggregateType: true,
                                clrType: this.columnClrType("factNumberOfDays"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "factNumberOfDays"),
                                    this.columnClrType("factNumberOfDays"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-fact-number-of-days={values.bidId}>
                                {formatValue(value, this.columnClrType("factNumberOfDays"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: `Маржа в день, ${organization.currencyData?.name}/день`,
                        accessor: "factMarginPerDay",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "FactMarginPerDay",
                                aggregateType: true,
                                clrType: this.columnClrType("factMarginPerDay"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {hiddenUserData(
                                    formatValue(
                                        this.aggregatedValue(leafRows, depth, "factMarginPerDay"),
                                        this.columnClrType("factMarginPerDay"),
                                    ),
                                    { type: "pricePerVal" },
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-fact-margin-per-day={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("factMarginPerDay")), {
                                    type: "pricePerVal",
                                })}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "ИТОГО маржа в день на ТС, руб/день",
                accessor: "totalMarginPerDay",
                ...typeSpecificProps({ clrType: "double" }),
                Cell: ({ value }) =>
                    hiddenUserData(formatValue(value, this.columnClrType("totalMarginPerDay")), { type: "price" }),
                Aggregated: ({ row: { leafRows, depth } }) => {
                    return (
                        <strong>
                            {hiddenUserData(
                                formatValue(
                                    this.aggregatedValue(leafRows, depth, "totalMarginPerDay"),
                                    this.columnClrType("totalMarginPerDay"),
                                ),
                                {
                                    type: "price",
                                },
                            )}
                        </strong>
                    );
                },
                Footer: () =>
                    this.footerVal({
                        propertyName: "TotalMarginPerDay",
                        aggregateType: true,
                        clrType: this.columnClrType("totalMarginPerDay"),
                    }),
            },
            {
                Header: "Отклонения по марже в день",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: `${organization.currencyData?.name}/день`,
                        accessor: "marginDeviationPerDay",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "MarginDeviationPerDay",
                                aggregateType: true,
                                clrType: this.columnClrType("marginDeviationPerDay"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {hiddenUserData(
                                    formatValue(
                                        this.aggregatedValue(leafRows, depth, "marginDeviationPerDay"),
                                        this.columnClrType("marginDeviationPerDay"),
                                    ),
                                    { type: "pricePerVal" },
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-margin-deviation-per-day={values.bidId}>
                                {formatValue(value, this.columnClrType("marginDeviationPerDay"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "%",
                        accessor: "marginDeviationPerDayInPercent",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "MarginDeviationPerDayInPercent",
                                aggregateType: true,
                                clrType: this.columnClrType("marginDeviationPerDayInPercent"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "marginDeviationPerDayInPercent"),
                                    this.columnClrType("marginDeviationPerDayInPercent"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-margin-deviation-per-day-in-percent={values.bidId}>
                                {formatValue(value, this.columnClrType("marginDeviationPerDayInPercent"))}
                            </span>
                        ),
                    },
                ],
            },
            {
                Header: "Значение за выбранный период",
                Footer: () => "",
                columns: [
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: "Кол-во дней на маршруте",
                        accessor: "numberOfDaysForPeriod",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "NumberOfDaysForPeriod",
                                aggregateType: true,
                                clrType: this.columnClrType("numberOfDaysForPeriod"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {formatValue(
                                    this.aggregatedValue(leafRows, depth, "numberOfDaysForPeriod"),
                                    this.columnClrType("numberOfDaysForPeriod"),
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-number-of-days-for-period={values.bidId}>
                                {formatValue(value, this.columnClrType("numberOfDaysForPeriod"))}
                            </span>
                        ),
                    },
                    {
                        ...typeSpecificProps({ clrType: "double" }),
                        Header: `Маржа всего, ${organization.currencyData?.name}`,
                        accessor: "totalMarginForPeriod",
                        Footer: () =>
                            this.footerVal({
                                propertyName: "TotalMarginForPeriod",
                                aggregateType: true,
                                clrType: this.columnClrType("totalMarginForPeriod"),
                            }),
                        Aggregated: ({ row: { leafRows, depth } }) => (
                            <strong>
                                {hiddenUserData(
                                    formatValue(
                                        this.aggregatedValue(leafRows, depth, "totalMarginForPeriod"),
                                        this.columnClrType("totalMarginForPeriod"),
                                    ),
                                    { type: "pricePerVal" },
                                )}
                            </strong>
                        ),
                        Cell: ({ value, row: { values } }) => (
                            <span data-total-margin-for-period={values.bidId}>
                                {hiddenUserData(formatValue(value, this.columnClrType("totalMarginForPeriod")), {
                                    type: "price",
                                })}
                            </span>
                        ),
                    },
                ],
            },
        ];
    };

    tableExtraProps = () => {
        const { marginalityValues } = this.props;
        const hiddenColumns = ["carLogistDisplayName", "carNumber", "driverDisplayName"];

        if (marginalityValues === "estimated") {
            hiddenColumns.push("factNumberOfDays", "factMarginPerDay");
        }
        if (marginalityValues === "fact") {
            hiddenColumns.push("estimatedNumberOfDays", "estimatedMarginPerDay");
        }

        return {
            initialState: {
                groupBy: ["activityType", "carLogistId", "carId", "driverId"],
                hiddenColumns,
            },
            data: this.formatData(this.props.data.data),
        };
    };
}
